// Single source of truth for the localStorage keys mobile auth reads from /
// writes to. Extracted because two independent implementations of "read the
// shared token" had drifted apart (audit-2026-09-07 §2): a native mobile
// login wrote ONLY `auth_token`, but syncQueue.js's own getToken() checked
// `token`/`mobileToken` and never looked at `auth_token` at all — so any
// action queued right after a native login carried no Authorization header,
// got 401'd, and was then silently dropped by flushQueue()'s "4xx won't fix
// by retry" rule. Logout also cleared only `auth_token`, so a stale `token`
// key (set by the desktop SPA's cross-surface SSO fallback) could shadow a
// later session's own auth_token on a shared/kiosk device — the two bugs
// compose into queued writes silently authenticating as the WRONG user.
//
// Order matters: it is the precedence both readers must agree on.
import { clearWorkforceRetryState } from '../../components/workforceExchangeQueue';
import { clearOperandiRetryState } from '../../components/operandiRetrySession';
import { forgetOfflineSession } from '../../auth/offlineSession';

export const SHARED_AUTH_TOKEN_KEYS = ['auth_token', 'token', 'mobileToken'];

export function readSharedToken(storage = typeof window !== 'undefined' ? window.localStorage : null) {
  if (!storage) return null;
  for (const key of SHARED_AUTH_TOKEN_KEYS) {
    try {
      const value = storage.getItem(key);
      if (value) return value;
    } catch {
      /* ignore — a blocked/unavailable storage must not crash auth */
    }
  }
  return null;
}

export function clearSharedAuthTokens(storage = typeof window !== 'undefined' ? window.localStorage : null) {
  clearOperandiRetryState();
  clearWorkforceRetryState();
  forgetOfflineSession();   // U-2: the remembered profile and menu go with the session
  if (!storage) return;
  for (const key of SHARED_AUTH_TOKEN_KEYS) {
    try {
      storage.removeItem(key);
    } catch {
      /* ignore */
    }
  }
}

/** Write one session token for both shells: mobile reads `auth_token` first, the desktop app reads `token`. */
export function persistSessionToken(token, storage = typeof window !== 'undefined' ? window.localStorage : null) {
  if (!storage || !token) return;
  storage.setItem('auth_token', token);
  storage.setItem('token', token);
  storage.removeItem('mobileToken');
}
